import { useTranslation } from 'react-i18next'
import React, { useMemo, useCallback } from 'react'
import { Divider, Grid, SvgIcon, Typography, useMediaQuery } from '@mui/material'
import PropTypes from 'prop-types'

import {
  AVATAR_SIZE,
  AVATAR_TYPES,
  AVATAR_VARIANT,
} from '@hapana/shared/web/components/shared/avatar/avatar.constants'
import SingleAvatarComponent from '@hapana/shared/web/components/shared/avatar/single-avatar.component'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import {
  BUTTON_SIZE,
  BUTTON_THEME,
  BUTTON_VARIANTS,
} from '@hapana/shared/web/components/shared/button/button.constants'
import BarcodeIcon from '@hapana/shared/web/styles/assets/svgs/barcode.svg'
import { formatDistanceToNowAbbreviated } from '@hapana/shared/utils/date.util'
import useSiteStore from '_stores/site.store'

import { styles } from './search-result-user-card.styles'

const SearchResultUserCard = ({
  clientId,
  firstName,
  lastName,
  email,
  profileImageURL,
  siteInformation,
  onClickManualCheckin,
}) => {
  const matchesDesktop = useMediaQuery((theme) => theme.breakpoints.up('sm'))
  const { t } = useTranslation(['checkIn'])
  const { currentSite } = useSiteStore()
  const siteId = currentSite?.siteId

  const handleCheckin = useCallback(() => {
    onClickManualCheckin(clientId, siteId)
  }, [clientId, siteId, onClickManualCheckin])

  const lastCheckInOn = useMemo(() => {
    const match = siteInformation.filter((siteInfo) => siteInfo.siteId === siteId)[0]
    const raw = match?.lastCheckInOn
    if (!raw) return null
    const date = new Date(Number(raw))
    return isNaN(date.getTime()) ? null : date
  }, [siteId, siteInformation])

  return (
    <>
      <Grid container sx={styles.resultsWrapper}>
        <Grid item md={1.5} xs={3} sm={1.5} sx={styles.avatarWrapper}>
          <SingleAvatarComponent
            variant={AVATAR_VARIANT.CIRCULAR}
            avatarType={AVATAR_TYPES.SINGLE_AVATAR}
            item={{
              alt: firstName,
            }}
            src={profileImageURL}
            size={matchesDesktop ? AVATAR_SIZE.SEARCH_RESULT : AVATAR_SIZE.EXTRA_LARGE}
          />
        </Grid>
        <Grid item container md={10.5} xs={8} sm={10.5} sx={styles.userWrapper}>
          <Grid item md={7} sm={7} container sx={styles.userInfo}>
            <Grid item sx={styles.userInfoWrapper}>
              <Typography typography="header3" color="gray.100">
                {firstName + ' ' + lastName}
              </Typography>
              <Typography typography="medium" color="gray.80">
                {email}
              </Typography>
            </Grid>
            {/* Disabled for this iteration */}
            {/* <Grid item>
              <ViewAllAlerts
                alerts={[
                  ALERT_TYPES.BIRTHDAY,
                  ALERT_TYPES.FIRST_SESSION,
                  ALERT_TYPES.HOME,
                  ALERT_TYPES.PAYMENT,
                ]}
              />
            </Grid> */}
          </Grid>
          <Grid item container md={5} sm={5} sx={styles.userOptions}>
            <Button
              sx={styles.linkButton}
              variant={BUTTON_VARIANTS.OUTLINED}
              size={BUTTON_SIZE.SMALL}
              theme={BUTTON_THEME.GRAY}
              onClick={handleCheckin}
            >
              {t('checkIn:checkIn')}
            </Button>
            {lastCheckInOn && (
              <Grid item container sx={styles.lastCheckin}>
                <SvgIcon component={BarcodeIcon} sx={styles.barcodeIcon} />
                <Grid item>
                  <Typography typography="medium" color="gray.56">
                    {t('checkIn:lastCheckIn')}
                  </Typography>
                </Grid>

                <Grid item>
                  <Typography typography="header4" color="gray.64" sx={styles.timeAgo}>
                    {formatDistanceToNowAbbreviated(lastCheckInOn)}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Divider sx={styles.divider} />
    </>
  )
}

SearchResultUserCard.propTypes = {
  clientId: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  profileImageURL: PropTypes.string,
  siteInformation: PropTypes.arrayOf(
    PropTypes.shape({
      siteId: PropTypes.string,
      lastCheckInOn: PropTypes.string,
    })
  ),
  onClickManualCheckin: PropTypes.func,
}

export default SearchResultUserCard
